// ============================================================
// DatesEditor.tsx — (Settlement redesign) "Add the dates": the first blocker row
// while either date is missing. There is no default settlement date. The two
// dates sit side by side and save together, as soon as both are there and valid;
// a draft needs a settlement date, so with no statement yet this is what creates
// it, in the name of whoever typed them. A refused save stops until the next
// edit; the reason shows beside the statement details.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import type { DraftFields, StatementDraft } from '../../../types/settlementStatement.types'
import ActionBox from '../../shared/ActionBox'
import DateInput from '../../shared/DateInput'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { NOTE } from '../../billing/billingUi'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'

export const RECEIVED_BEFORE_SETTLEMENT = 'The damages can’t have been received before the settlement date.'
const SAVE_MS = 700

const DatesEditor: React.FC<{
  draft: Pick<StatementDraft, 'settlementDate' | 'damagesReceivedAt'>
  busy: boolean
  save: (dates: Pick<DraftFields, 'settlementDate' | 'damagesReceivedAt'>) => Promise<boolean>
  onEdit?: () => void
  refused?: (stopped: boolean) => void
  onDiscard?: () => void
}> = ({ draft, busy, save, onEdit, refused, onDiscard }) => {
  const [settlement, setSettlement] = useState<string | null>(draft.settlementDate ?? null)
  const [received, setReceived] = useState<string | null>(draft.damagesReceivedAt ?? null)
  const [ok, setOk] = useState({ settlement: true, received: true })
  const [stopped, setStopped] = useState(false)
  const changed = settlement !== (draft.settlementDate ?? null) || received !== (draft.damagesReceivedAt ?? null)
  useUnsavedChangesGuard(changed)
  const orderErr = settlement && received && received < settlement ? RECEIVED_BEFORE_SETTLEMENT : null
  const ready = !!settlement && !!received && ok.settlement && ok.received && !orderErr && changed
  const latest = useRef({ settlement, received })
  latest.current = { settlement, received }
  const edited = (): void => { setStopped(false); refused?.(false); onEdit?.() }
  useEffect(() => {
    if (!ready || stopped || busy) return undefined
    const t = window.setTimeout(() => {
      void save({ settlementDate: latest.current.settlement ?? undefined, damagesReceivedAt: latest.current.received })
        .then((saved) => { if (!saved) { setStopped(true); refused?.(true) } })
    }, SAVE_MS)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlement, received, ready, stopped, busy])
  const fields = (
    <span className="block mt-[8px]">
      <span className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-x-[12px] gap-y-[10px]">
        <span className="block min-w-0">
          <FormField label="Settlement date" required variant="info">
            <DateInput value={settlement} allowFuture={false} ariaLabel="Settlement date" onChange={(v) => { setSettlement(v); edited() }} onValidityChange={(v) => setOk((o) => ({ ...o, settlement: v }))} />
          </FormField>
        </span>
        <span className="block min-w-0">
          <FormField label="Date damages received" required variant="info">
            <DateInput value={received} allowFuture={false} ariaLabel="Date damages received" onChange={(v) => { setReceived(v); edited() }} onValidityChange={(v) => setOk((o) => ({ ...o, received: v }))} />
          </FormField>
        </span>
      </span>
      <FieldError message={orderErr ?? undefined} />
      <span className={`${NOTE} flex items-center gap-[10px] mt-[4px]`} role="status" data-dates-status="">
        {busy ? 'Saving…' : stopped ? 'Not saved' : 'Both print on the statement. They save together.'}
        {changed && onDiscard && <button type="button" className="text-[13px] font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2" onClick={onDiscard}>Discard my change</button>}
      </span>
    </span>
  )
  return <ActionBox ariaLabel="Add the dates" title="Add the dates" message={fields} />
}

export default DatesEditor
